import { motion } from "framer-motion";
import { progressFraction, chapterForScreen } from "../chapters";
import { DEFAULT_BAND, type Band } from "../band";
import type { AssessmentV2State } from "../types";
import { levelFor, chapterMap } from "../delight";
import { LevelBadge } from "./LevelBadge";
import { cn } from "@/lib/utils";

/**
 * Meaningful progress. The CHAPTER NAME is the progress indicator — never
 * "step N of M", never a percentage. Alongside it: a chapter journey map
 * (one pill per chapter, the current one lit) and the learner's level.
 *
 * Presentation only — the fraction still comes from the canonical walk.
 */
export function ProgressBar({
  screenId, band = DEFAULT_BAND, state,
}: { screenId: string; band?: Band; state?: AssessmentV2State }) {
  const frac = progressFraction(screenId, state);
  const chapter = chapterForScreen(screenId, band);
  const level = levelFor(frac);
  const { chapters, index } = chapterMap(screenId, band);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="text-base md:text-lg font-display font-medium text-foreground leading-snug">
          {chapter?.title ?? ""}
        </h2>
        <LevelBadge level={level} />
      </div>

      {/* Chapter journey map — unlocked chapters stay lit. */}
      <div className="flex items-center gap-1.5" aria-hidden>
        {chapters.map((c, i) => (
          <div
            key={c.id}
            className={cn(
              "h-1.5 flex-1 rounded-full overflow-hidden",
              i < index ? "bg-primary/50" : "bg-secondary",
            )}
          >
            {i === index && (
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={false}
                animate={{ width: `${Math.round(chapterFrac(frac, chapters.length, index) * 100)}%` }}
                transition={{ type: "spring", stiffness: 180, damping: 26 }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Kept for assistive tech and tests: a real progressbar role. */}
      <div
        className="sr-only"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(frac * 100)}
        aria-label={
          chapter
            ? `${chapter.title} — chapter ${index + 1} of ${chapters.length}`
            : "Progress"
        }
      />
    </div>
  );
}

/** How far through the current chapter's slice of the walk we are. */
function chapterFrac(frac: number, total: number, index: number): number {
  if (total <= 0 || index < 0) return 0;
  const slice = 1 / total;
  return Math.min(1, Math.max(0.08, (frac - index * slice) / slice));
}
